import { Component, EventEmitter, inject, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { Pagination } from '../../../../../../models/pagination-table.model';
import { TableOptions } from '../../../../../../services/i-bary-table-fetch-service';
import { PATHS } from '../../../../../../models/bary-paths.model';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe } from '@angular/common';
import { mapAsBody } from '../../../../util/request-util';
import { BillService } from '../../../../../../services/bill-service';
import { TransportOrderView } from '../../../../../../models/transport-order-view.model';
import { CtaAction } from '../../../../../../models/cta.action';
import { Transporter } from '../../../../../../models/transporter.model';
import { CurrentDataService } from '../../../../../../services/current-data-service';
import { BaryModalService } from '../../../../../../services/bary-modal.service';
import { AlertService } from '../../../../../../services/alert.service';
import { EdiTransporterConfigurationService } from '../../../../../../services/edi-transporter-configuration.service';
import { LIST_ACTIONS } from '../../../../../../models/enums/action-cta.enum';
import { LIST_REFUNDED_STATUS } from '../../../../../../models/basket-order-refunded-status.model';

type TransportOrderViewWithCTA = TransportOrderView & {
  ctaActions: CtaAction[];
};

@Component({
  selector: 'jhi-transports-list',
  templateUrl: './transports-list.component.html',
  styleUrls: ['./transports-list.component.scss'],
})
export class TransportsListComponent implements OnInit, OnChanges {
  readonly #router = inject(Router);
  readonly #currentDataService = inject(CurrentDataService);
  readonly #translateService = inject(TranslateService);
  readonly #baryModalService = inject(BaryModalService);
  readonly #alertService = inject(AlertService);
  readonly #datePipe = inject(DatePipe);
  readonly #ediTransporterConfigurationService = inject(EdiTransporterConfigurationService);
  readonly #billService = inject(BillService);

  @Input() transportOrders: TransportOrderViewWithCTA[] = [];
  @Input() pagination!: Pagination;
  @Input() tableOptions?: TableOptions;
  @Output() paginationChange = new EventEmitter<Pagination>();

  public PATHS = PATHS;
  public LIST_REFUNDED_STATUS = LIST_REFUNDED_STATUS;
  public today: Date = new Date();
  public transporter!: Transporter;
  public actions: CtaAction[] = [];
  public hasEDI = false;

  public ngOnInit(): void {
    this.#currentDataService.getCurrentTransporter().subscribe(value => {
      if (value) {
        this.transporter = value;
        this.#ediTransporterConfigurationService
          .retrieveConfiguration(this.transporter.id)
          .pipe(mapAsBody)
          .subscribe(it => (this.hasEDI = !!it.ediId));
      }
    });
  }

  public updateActions(transportOrder: TransportOrderViewWithCTA): CtaAction[] {
    const actions = [];
    if (transportOrder?.basketOrder?.id) {
      actions.push({
        icon: 'fa-file-invoice',
        label: this.#translateService.instant('label-cta.bill'),
        disabled: false,
        action: LIST_ACTIONS.BILL,
        dataIdCypress: 'action-bill',
      } as CtaAction);
    }
    actions.push({
      label: this.#translateService.instant('label-cta.detail'),
      action: LIST_ACTIONS.DETAIL,
      disabled: false,
      icon: 'fa-eye',
      dataIdCypress: 'action-detail',
    } as CtaAction);
    transportOrder.pods?.forEach(it => {
      if (it.podUrl) {
        actions.push({
          icon: 'fa-box-open',
          label: this.#translateService.instant('label-cta.pod'),
          action: LIST_ACTIONS.POD,
          url: it.podUrl,
          dataIdCypress: `action-pod-${it.id}`,
          disabled: false,
        } as CtaAction);
      }
    });
    if (this.tableOptions?.isModerator) {
      actions.push({
        icon: 'fa-rotate-left',
        label: this.#translateService.instant('label-cta.refund'),
        action: transportOrder.refundable ? null : LIST_ACTIONS.OPEN_REFUND_MODAL,
        tooltip: transportOrder.refundable ? this.#translateService.instant('tooltip.' + transportOrder.refundable.code) : null,
      } as CtaAction);
    }
    return actions;
  }

  public transition(): void {
    this.paginationChange.emit(this.pagination);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['transportOrders'].currentValue) {
      this.transportOrders = changes['transportOrders'].currentValue;
      this.transportOrders?.forEach(it => {
        it.ctaActions = this.updateActions(it);
      });
    }
  }

  public getTransportTypeIcon(transportOrderView?: TransportOrderViewWithCTA): string {
    return 'icon-' + transportOrderView?.getOptionsTypeSelector().icon;
  }

  public getTotalUMTooltip(transportOrder: TransportOrderViewWithCTA): string {
    let result = '';
    if (transportOrder.totalPallets) {
      result += `${transportOrder.totalPallets} ${
        transportOrder.totalPallets > 1 ? this.#translateService.instant('LABEL.PALLETS') : this.#translateService.instant('LABEL.PALLET')
      }`;
    }
    if (transportOrder.totalParcel) {
      if (result.length > 0) {
        result += ', ';
      }
      result += `${transportOrder.totalParcel} ${this.#translateService.instant('LABEL.PARCELS')}`;
    }
    if (transportOrder.totalOtherUM) {
      if (result.length > 0) {
        result += ', ';
      }
      result += `${transportOrder.totalOtherUM} ${this.#translateService.instant('LABEL.UMS')}`;
    }
    return result;
  }

  public getTooltipFlexibility(transportOrder: TransportOrderView): string {
    return this.#translateService.instant('TRANSPORTS_LIST.TOOLTIP_FLEXIBILITY', {
      date1: this.#datePipe.transform(transportOrder.deliveryDate?.toDate(), 'dd/MM/yyyy'),
      date2: this.#datePipe.transform(transportOrder.deliveryMaxDate?.toDate(), 'dd/MM/yyyy'),
    }) as string;
  }

  public goToDetails(basketOrderId: number): void {
    let transportViewUrl = PATHS.USER_MY_TRANSPORTS_TRANSPORT_ID;
    if (this.tableOptions?.isModerator) {
      transportViewUrl = PATHS.MIDDLE_TRANSPORT_ID;
    }
    this.#router.navigateByUrl(transportViewUrl.withId(basketOrderId).toString());
  }

  public goToPod(urlPod: string | undefined): void {
    window.open(urlPod, '_blank');
  }

  public goToBill(basketOrderId: number | undefined): void {
    if (basketOrderId) {
      this.#billService.browserPdf(basketOrderId);
    }
  }

  public onSelectAction(cta: CtaAction, transport: TransportOrderViewWithCTA): void {
    if (cta.action === LIST_ACTIONS.BILL) {
      this.goToBill(transport?.basketOrder?.id);
    } else if (cta.action === LIST_ACTIONS.DETAIL && transport?.id) {
      this.goToDetails(transport.id);
    } else if (cta.action === LIST_ACTIONS.POD) {
      this.goToPod(cta.url);
    } else if (cta.action === LIST_ACTIONS.OPEN_REFUND_MODAL) {
      this.goToBillRefund(transport);
    }
  }

  public goToBillRefund(transport: TransportOrderViewWithCTA): void {
    this.#baryModalService
      .openTransportRefund(transport)
      .action()
      .subscribe({
        next: (val: any): void => {
          if (val.first || val.submit) {
            this.#alertService.addAlert({
              message: this.#translateService.instant('refund.successTransport'),
              type: 'success',
            });
          } else {
            this.#alertService.addAlert({
              message: this.#translateService.instant('refund.error'),
              type: 'danger',
            });
          }
          this.transition();
        },
      });
  }
}
